import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ErrorMessage } from '@hookform/error-message';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import CatalogItemActionsComponent from '../../../features/Catalogs/CatalogItemActionsComponent';
import ConfirmationModal from '../../../features/ConfirmationModal';
import FormButtons from '../../../features/FormButtons';
import { useAppDispatch, useAppSelector, useSetServerError } from '../../../hooks';
import { Skill } from '../../../models/catalogs';
import { clearCatalogTextFieldErrors, createSkill, editSkill, getLookupByName } from '../../../store';
import { StyledSelect, StyledSelectError } from '../../../ui/Select';

import { IFormInput } from './Skills';

import styles from '../CatalogsLayout.module.scss';

export const SkillForm: FC<{
    name: string;
    activeSkill: Skill | null;
    setActiveSkill: (activeSkill: Skill | null) => void;
    createOrUpdate: string | null;
    isDrawerOpen: boolean;
    setCreateOrUpdate: Function;
    setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
    fulfilledStatusHandler: Function;
}> = ({
          name,
          activeSkill,
          setActiveSkill,
          createOrUpdate,
          isDrawerOpen,
          setCreateOrUpdate,
          setIsDrawerOpen,
          fulfilledStatusHandler,
      }): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        setError,
        formState: { errors },
        clearErrors,
        control,
    } = useForm<IFormInput>();

    const skillSets = useAppSelector((state) => state.lookups.skillSets);
    const skillsErrors = useAppSelector((state) => state.catalogs.skillsErrors);
    const isCreatingOrUpdatingLoading = useAppSelector(
        (state) => state.catalogs.isCreatingOrUpdatingLoading,
    );

    const onSubmit: SubmitHandler<IFormInput> = async (
        formData: IFormInput,
    ) => {
        if (createOrUpdate === "create") {
            const response = await dispatch(
                createSkill({
                    name: formData.name,
                    skillSetId: formData.skillSetId,
                    description: formData.description,
                    skillsEndpointPath: name,
                }),
            );

            if (response.meta.requestStatus === "fulfilled") {
                fulfilledStatusHandler("notifications.successfullyCreated");
                setIsDrawerOpen(false);
                setActiveSkill(null);
            }
            return;
        }

        if (activeSkill && createOrUpdate === "update") {
            const response = await dispatch(
                editSkill({
                    id: activeSkill.id,
                    name: formData.name,
                    skillSetId: formData.skillSetId,
                    description: formData.description,
                    skillsEndpointPath: name,
                }),
            );

            if (response.meta.requestStatus === "fulfilled") {
                fulfilledStatusHandler("notifications.successfullySaved");
                setIsDrawerOpen(false);
                // setActiveSkill(null);
            }
        }
    };

    const formClose = () => {
        if (formChangesChecking()) setConfirmationModalOpen(true);
        else closeFormDrawer();
    };

    const closeFormDrawer = () => {
        setCreateOrUpdate(null);
        setIsDrawerOpen(false);
    };

    const closeModalConfirm = () => {
        setFormData(null);
        setIsDrawerOpen(false);
        setConfirmationModalOpen(false);
    };

    // returns true, if form was updated
    const formChangesChecking = () => {
        if (createOrUpdate === "create") {
            return Boolean(
                getValues("name") ||
                getValues("description") ||
                getValues("skillSetId"),
            );
        } else {
            return Boolean(
                getValues("name") !== activeSkill?.name ||
                getValues("description") !== activeSkill?.description ||
                getValues("skillSetId") !== activeSkill?.skillSetId,
            );
        }
    };

    const setFormData = (fields: IFormInput | null) => {
        setValue("name", fields?.name || "");
        setValue("description", fields?.description || "");
        setValue("skillSetId", fields?.skillSetId!);
    };

    useSetServerError({
        serverError: skillsErrors,
        setError,
        clearErrors,
        errorNames: ["name", "description", "parentId"],
    });

    useEffect(() => {
        if (isDrawerOpen) {
            dispatch(getLookupByName("skillset"));
            setFormData(activeSkill);
        } else {
            clearErrors();
            dispatch(clearCatalogTextFieldErrors());
        }
    }, [dispatch, isDrawerOpen]);

    return (
        <>
            <CatalogItemActionsComponent
                name={name}
                isDrawerOpen={isDrawerOpen}
                createOrUpdate={createOrUpdate}
                closeFormDrawer={formClose}
            >
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className={styles.formInputWrapper}>
                        <label
                            htmlFor="name"
                            className={styles.drawerTopSubTitle}
                        >
                            {t("data.name")}
                        </label>
                        <div>
                            <input
                                {...register("name", {
                                    required: t(
                                        "validation.requiredField",
                                    ) as string,
                                    maxLength: {
                                        value: 30,
                                        message: t("validation.maxLength30"),
                                    },
                                })}
                                id="name"
                                type="text"
                                autoComplete="off"
                                placeholder={t("data.name") as string}
                                className={
                                    errors?.name
                                        ? styles.catalogsDrawerInputError
                                        : styles.catalogsDrawerInput
                                }
                            />
                            <ErrorMessage
                                name="name"
                                errors={errors}
                                render={({ message }) => <span>{message}</span>}
                            />
                        </div>
                        <label
                            htmlFor="description"
                            className={styles.drawerTopSubTitle}
                        >
                            {t("data.description")}
                        </label>
                        <div>
                            <input
                                {...register("description", {
                                    maxLength: {
                                        value: 150,
                                        message: t("validation.maxLength150"),
                                    },
                                })}
                                id="description"
                                type="text"
                                autoComplete="off"
                                placeholder={t("data.description") as string}
                                className={
                                    errors?.description
                                        ? styles.catalogsDrawerInputError
                                        : styles.catalogsDrawerInput
                                }
                            />
                            <ErrorMessage
                                name="description"
                                errors={errors}
                                render={({ message }) => <span>{message}</span>}
                            />
                        </div>
                        <FormControl
                            fullWidth
                            sx={{ margin: ".4rem 0 1.7rem 0" }}
                        >
                            <span className={styles.drawerTopSubTitle}>
                                {t("data.skillSet")}
                            </span>
                            <Controller
                                name="skillSetId"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        input={
                                            errors?.skillSetId ? (
                                                <StyledSelectError />
                                            ) : (
                                                <StyledSelect />
                                            )
                                        }
                                    >
                                        {skillSets.map((item) => (
                                            <MenuItem
                                                key={item.id}
                                                value={item.id}
                                            >
                                                {item.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            <ErrorMessage
                                name="skillSetId"
                                errors={errors}
                                render={({ message }) => (
                                    <span className={styles.selectErrorMsg}>
                                        {message}
                                    </span>
                                )}
                            />
                        </FormControl>
                    </div>
                    <FormButtons
                        isLoading={isCreatingOrUpdatingLoading}
                        createOrUpdate={createOrUpdate}
                        cancelHandler={formClose}
                    />
                </form>
            </CatalogItemActionsComponent>

            <ConfirmationModal
                isModalOpen={isConfirmationModalOpen}
                setIsModalOpen={setConfirmationModalOpen}
                actionName={"Close"}
                instanceName=""
                message={"closeFormWithoutSaving"}
                handleAction={() => closeModalConfirm()}
            />
        </>
    );
};
